import { ReactElement, useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

let DEFAULT_DURATION_MS = 4000

type SaveConfirmationProps = {
  // Timestamp of the most recent successful save — a new value re-shows the
  // confirmation, null keeps it hidden.
  savedAt: number | null
  message?: string
  durationMs?: number
}

/**
 * Transient "Saved" confirmation for the inline-edit fields in the Course
 * Planning dashboard. The wrapper live region is always mounted so the message
 * is announced when it appears rather than on mount.
 */
export default function SaveConfirmation({
  savedAt,
  message = 'Saved',
  durationMs = DEFAULT_DURATION_MS,
}: SaveConfirmationProps): ReactElement {
  let [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!savedAt) {
      setVisible(false)
      return undefined
    }
    setVisible(true)
    let timeout = setTimeout(() => setVisible(false), durationMs)
    return () => clearTimeout(timeout)
  }, [savedAt, durationMs])

  return (
    <Box role="status" aria-live="polite" aria-atomic="true">
      {visible && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
          <Typography
            variant="body2"
            sx={{ color: 'success.main', fontWeight: 500 }}
          >
            {message}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
